import { apiBaseUrl } from '@/lib/app-auth-session'
import { getFreshAccessToken } from '@/lib/auth/get-fresh-access-token'

export interface ApiFetchOptions extends Omit<RequestInit, 'headers' | 'body'> {
  /** Plain object body that will be serialized as JSON. Mutually exclusive with `rawBody`. */
  json?: unknown
  /** Pre-built body (FormData, Blob, string). When provided, no Content-Type is set automatically. */
  rawBody?: BodyInit
  /** Extra headers, merged on top of Authorization + Content-Type. */
  headers?: Record<string, string>
  /** Disable retry-on-401-with-refresh (used by the refresh request itself). */
  noRetry?: boolean
}

/**
 * Authenticated `fetch` against the FairHelm API. Adds Bearer header from
 * `getFreshAccessToken` and retries once with `forceRefresh` on a 401.
 *
 * Path can be absolute (`https://...`) or path-only (`/api/app/v1/...`); in the
 * latter case `NEXT_PUBLIC_API_URL` is prepended.
 */
export async function apiFetch(
  path: string,
  options: ApiFetchOptions = {},
): Promise<Response> {
  const url = path.startsWith('http') ? path : `${apiBaseUrl()}${path}`
  const buildInit = async (forceRefresh: boolean): Promise<RequestInit> => {
    const token = await getFreshAccessToken({ forceRefresh })
    const headers: Record<string, string> = { ...(options.headers ?? {}) }
    if (token) headers.Authorization = `Bearer ${token}`
    let body: BodyInit | undefined = options.rawBody
    if (options.json !== undefined) {
      body = JSON.stringify(options.json)
      if (!headers['Content-Type']) headers['Content-Type'] = 'application/json'
    }
    return { ...options, headers, body }
  }

  const first = await fetch(url, await buildInit(false))
  if (first.status !== 401 || options.noRetry) return first
  return fetch(url, await buildInit(true))
}

/** Throws when not 2xx; returns parsed JSON otherwise. */
export async function apiFetchJson<T>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const res = await apiFetch(path, options)
  if (!res.ok) {
    let detail = res.statusText
    try {
      const j = (await res.json()) as { detail?: string | object }
      if (typeof j.detail === 'string') detail = j.detail
    } catch {
      /* keep statusText */
    }
    throw new Error(`API ${res.status}: ${detail}`)
  }
  return (await res.json()) as T
}
